/**
 * The books shared with an invitee, on their device (part 38c; decision 75; principle 10; principles GP1–GP6, tables CL,
 * CC, NV and KG): accepting an invite link (the claim, then the keyring made or its entry added under one grant), the
 * check that keeps the cache to what the relay serves (each claim's settings, role keys, copies and notes; everything the
 * relay no longer lists dropped, and anything past its end date by this device's clock), each item opened only to be
 * shown, and the invitee's own notes posted, edited and deleted where `mayPost` allows. Every answer passes `wire.ts`'s
 * readers, every copy and note opens only at its own place, and nothing here touches a book store. Every effect is
 * injected, so it runs against the relay in the same process in tests.
 */
import { encodeShareNote, readInviteCopy, readShareNote, toHex } from '@cockatrice/core';
import type { InviteCopy, InviteManifest, ItemAnchor, ShareNote } from '@cockatrice/core';
import { claimProof, inviteIdOf, openCopy, openNote, sealNote, unwrapCopyKey, unwrapRoleKey, WRITER_TAG_BYTES } from '@cockatrice/crypto';
import type { KeyGroup } from '@cockatrice/storage';
import { fromBase64Url, toBase64Url } from '../account/bytes.ts';
import type { RelayError, Reply } from '../account/client.ts';
import { MAX_COPY_BYTES } from '../share/copies.ts';
import type { PulledNote, WireNoteGroup } from '../share/invite-wire.ts';
import { readNoteSeq, readOk } from '../share/invite-wire.ts';
import { newKeyring, openEntries, openKeyring, rewrapKeyring, sealEntry } from './keyring.ts';
import type { PasswordKeys, Random } from './keyring.ts';
import type { CachedClaim, CachedItem, HeldKeyring, HeldNote, HeldRoleKey, PendingInvite, SharedStore } from './store.ts';
import { claimBody, copyBody, keyBody, keyringBody, noteBody, noteRefBody, notesBody, readClaimed, readClaims, readInviteeNotes, readKeyring } from './wire.ts';
import type { Granted, ListedClaim, WireKeyring } from './wire.ts';

/**
 * Table LO: `store` whose writes of an account's (keyring, claims, items, their drops) are skipped unless `live()`; the
 * waiting invite's pair isn't an account's and passes, as every read does.
 */
export function guarded(store: SharedStore, live: () => boolean): SharedStore {
  const unless =
    <A extends unknown[]>(write: (...args: A) => Promise<void>) =>
    (...args: A): Promise<void> =>
      live() ? write(...args) : Promise.resolve();
  return {
    pending: () => store.pending(),
    keepPending: (pending) => store.keepPending(pending),
    dropPending: () => store.dropPending(),
    keyring: (account) => store.keyring(account),
    keepKeyring: unless((account: string, keyring: HeldKeyring) => store.keepKeyring(account, keyring)),
    claims: (account) => store.claims(account),
    keepClaim: unless((account: string, claim: CachedClaim) => store.keepClaim(account, claim)),
    items: (account, invite) => store.items(account, invite),
    keepItem: unless((account: string, invite: string, item: CachedItem) => store.keepItem(account, invite, item)),
    dropItems: unless((account: string, invite: string, items: readonly string[]) => store.dropItems(account, invite, items)),
    dropClaims: unless((account: string, invites: readonly string[]) => store.dropClaims(account, invites)),
    clear: unless((account: string) => store.clear(account)),
    accounts: () => store.accounts(),
    close: () => {
      store.close();
    },
  };
}

/** The relay as an invitee's session reaches it (the account's `get`, `post` and `postForBytes`). */
export interface InviteeRelay {
  get<T>(path: string, read: (value: unknown) => T | null): Promise<Reply<T>>;
  post<T>(path: string, body: unknown, read: (value: unknown) => T | null): Promise<Reply<T>>;
  postForBytes(path: string, body: unknown, most: number): Promise<Reply<Uint8Array<ArrayBuffer>>>;
}

/** What the shares run on, for one account. */
export interface SharesDeps {
  readonly account: string;
  readonly relay: InviteeRelay;
  readonly store: SharedStore;
  readonly subtle: SubtleCrypto;
  readonly random: Random;
  readonly now: () => number;
  /** Table LO: whether `account` is still the one logged in here (absent: always); writes stop as soon as it isn't. */
  readonly live?: () => boolean;
}

/** Table CL's outcomes of accepting an invite. */
export type AcceptOutcome =
  | { readonly kind: 'accepted'; readonly invite: string }
  /** CL2–CL4, CL6–CL8: the relay's word (the UI words each). */
  | { readonly kind: 'refused'; readonly error: RelayError }
  /** KG4: the account's keyring doesn't open under this password (another record, or altered). */
  | { readonly kind: 'keyring-refused' }
  /** CL5: the relay out of reach or its answer unreadable: the invite kept for another try. */
  | { readonly kind: 'failed' }
  /** Deep-fix r2, PW2: no invite waits here any more (another tab claimed it or put it away). */
  | { readonly kind: 'nothing' };

/** What a check found. */
export type CheckOutcome = { readonly kind: 'checked'; readonly unavailable: readonly string[] } | { readonly kind: 'offline' };

/** A note the invitee writes (table NV): a new one on a chapter or in the Notebook, or a reply to a note by its ID. */
export type Draft =
  | { readonly kind: 'note'; readonly chapter: string | null; readonly anchor: ItemAnchor | null; readonly title: string; readonly section: string; readonly text: string }
  | { readonly kind: 'reply'; readonly to: string; readonly text: string };

/** What a post or delete did. */
export type NoteOutcome = { readonly kind: 'done' } | { readonly kind: 'refused'; readonly error: RelayError } | { readonly kind: 'not-allowed' } | { readonly kind: 'failed' };

/** The words that end an invite link for good (CL3, CL4): the pair is dropped. */
const FINAL: readonly RelayError[] = ['invite-claimed', 'invite-ended', 'invite-gone'];

/** IP9 (part 38a's `readableGroups`): the groups a claim reads. */
export function readableGroups(claim: Pick<CachedClaim, 'role' | 'notes'>): readonly KeyGroup[] {
  return claim.notes === 'none' ? [] : [claim.role, 'everyone'];
}

/** NV, IP9 (part 38a's `mayPost`): whether the claim may post in `group` (its own thread being `person`). */
export function mayPost(claim: Pick<CachedClaim, 'role' | 'notes'>, group: WireNoteGroup): boolean {
  if (group === 'person') return claim.notes !== 'none';
  return claim.notes === 'write' && readableGroups(claim).includes(group);
}

/** NV: where a new note of the claim goes: its role group when it writes notes, its own thread when it reads them, nowhere at `none`. */
export function newNoteGroup(claim: Pick<CachedClaim, 'role' | 'notes'>): WireNoteGroup | null {
  if (claim.notes === 'write') return claim.role;
  return claim.notes === 'read' ? 'person' : null;
}

/** IA15: whether version `next` is later than `current` (16 bytes each, big-endian). */
export function isLater(next: Uint8Array, current: Uint8Array): boolean {
  for (let index = 0; index < Math.max(next.length, current.length); index += 1) {
    const a = next[index] ?? -1;
    const b = current[index] ?? -1;
    if (a !== b) return a > b;
  }
  return false;
}

/** CC9: whether `claim` has passed its end date at `now` (this device's clock: offline too). */
export function isPast(claim: Pick<CachedClaim, 'endsAt'>, now: number): boolean {
  return claim.endsAt !== null && claim.endsAt <= now;
}

/** Bytes of a base64url ID of 16 bytes; null otherwise. */
function idBytes(id: string): Uint8Array<ArrayBuffer> | null {
  const bytes = fromBase64Url(id);
  return bytes?.length === 16 ? bytes : null;
}

/** 32 lowercase hex digits as their 16 bytes; null otherwise. */
function hexBytes(hex: string): Uint8Array<ArrayBuffer> | null {
  if (!/^[0-9a-f]{32}$/u.test(hex)) return null;
  const out = new Uint8Array(16);
  for (let index = 0; index < 16; index += 1) out[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
  return out;
}

/** DI5: the item of invite `invite`'s manifest (part 38b's `manifestItem`, from the invite's ID in hex). */
export function manifestOf(invite: string): string | null {
  const bytes = idBytes(invite);
  return bytes === null ? null : `m-${toHex(bytes)}`;
}

/** An invitee's books shared with them, for one account. */
export class Shares {
  private readonly deps: SharesDeps;
  /** CC12: one check at a time in this page. */
  private running: Promise<CheckOutcome> | null = null;
  /** Deep-fix r5: the pass that follows the running check, when one was asked for meanwhile. */
  private again: Promise<CheckOutcome> | null = null;
  /** Table LO: closed at the account's logout; from then on nothing of this account is written. */
  private live = true;
  /** Table LO: every write this instance has under way (a check's, an accept's, a note's), so a logout waits them out. */
  private readonly flight = new Set<Promise<unknown>>();
  /** Deep-fix r4: whether work may go on (not closed, the account still logged in): a check stops between requests. */
  private readonly alive: () => boolean;

  constructor(deps: SharesDeps) {
    // LO1–LO6: every write of the account's goes through the guard, refused once closed or once another account (or none)
    // is logged in (`deps.live`), so work in flight at a logout never puts anything back.
    const live = (): boolean => this.live && (deps.live?.() ?? true);
    this.alive = live;
    this.deps = { ...deps, store: guarded(deps.store, live) };
  }

  /** Table LO: whether this instance has been closed (it writes nothing then). A method: it changes across an `await`. */
  closed(): boolean {
    return !this.live;
  }

  /** LO1–LO6: closed (no write of this account's from now on), resolved once every write under way has settled. */
  async close(): Promise<void> {
    this.live = false;
    while (this.flight.size > 0) await Promise.allSettled([...this.flight]);
  }

  /** `work` counted in flight until it settles (`close` waits for it). */
  private track<T>(work: Promise<T>): Promise<T> {
    this.flight.add(work);
    // Let go once settled, whichever way (a rejection stays the caller's to handle).
    void Promise.allSettled([work]).then(() => this.flight.delete(work));
    return work;
  }

  /** The claims cached, those past their end dropped first (CC9). */
  async claims(): Promise<readonly CachedClaim[]> {
    const { store, account, now } = this.deps;
    const cached = await store.claims(account);
    const past = cached.filter((claim) => isPast(claim, now()));
    if (past.length > 0) await store.dropClaims(account, past.map((claim) => claim.invite));
    return cached.filter((claim) => !isPast(claim, now()));
  }

  /**
   * KG1, KG2: the account's keyring opened with a typed password's `keys` and kept on this device, so its entries open
   * at each check; `none` before the first claim, `refused` when it doesn't open (KG4: nothing kept).
   */
  unlock(keys: PasswordKeys): Promise<'opened' | 'none' | 'refused' | 'failed'> {
    return this.track(this.unlocking(keys));
  }

  private async unlocking(keys: PasswordKeys): Promise<'opened' | 'none' | 'refused' | 'failed'> {
    const wire = await this.deps.relay.get('/v1/keyring', readKeyring);
    if (wire.kind !== 'ok') return 'failed';
    const opened = await openKeyring(this.deps.subtle, this.deps.account, keys.wrapping, wire.value);
    if (opened.kind !== 'opened') return opened.kind;
    await this.deps.store.keepKeyring(this.deps.account, opened.keyring);
    return 'opened';
  }

  /**
   * Table CL: `pending` claimed for this account with the password's `keys` and `grant` (a fresh grant of this device,
   * GP3), then its person key stored in the keyring under that grant: the keyring made with it when the account has none
   * (CL1), its entry added when it has one (opened with `keys` when this device doesn't hold it, CL7). The pair is dropped
   * once the entry is stored, or when the invite can never be claimed (CL3, CL4); kept otherwise (CL2, CL5, CL9).
   */
  accept(pending: PendingInvite, keys: PasswordKeys, grant: string): Promise<AcceptOutcome> {
    return this.track(this.accepting(pending, keys, grant));
  }

  private async accepting(pending: PendingInvite, keys: PasswordKeys, grant: string): Promise<AcceptOutcome> {
    const { subtle, relay, store } = this.deps;
    const proof = await claimProof(subtle, pending.claim);
    const id = await inviteIdOf(subtle, proof);
    const claimed = await relay.post('/v1/invites/claim', claimBody(id, proof), readClaimed);
    if (claimed.kind === 'failed') return { kind: 'failed' };
    if (claimed.kind === 'refused') {
      if (FINAL.includes(claimed.error)) await store.dropPending();
      return { kind: 'refused', error: claimed.error };
    }
    // GP5: the claim's answer names the invite the proof derives, or it is not this invite's.
    if (claimed.value.invite !== toBase64Url(id)) return { kind: 'failed' };
    const stored = await this.storeKey(claimed.value, pending.key, keys, grant);
    if (stored.kind !== 'accepted') return stored;
    await store.dropPending();
    // Deep-fix r4: the check is the caller's to start (a login never waits on every copy's download).
    return stored;
  }

  /** CL1, CL7: the person key `person` of `granted` stored in the account's keyring under `grant`. */
  private async storeKey(granted: Granted, person: Uint8Array<ArrayBuffer>, keys: PasswordKeys, grant: string): Promise<AcceptOutcome> {
    const { subtle, random, relay, store, account } = this.deps;
    let held = await store.keyring(account);
    if (held === null) {
      const wire = await relay.get('/v1/keyring', readKeyring);
      if (wire.kind !== 'ok') return { kind: 'failed' };
      const opened = await openKeyring(subtle, account, keys.wrapping, wire.value);
      if (opened.kind === 'refused') return { kind: 'keyring-refused' };
      if (opened.kind === 'none') return this.makeKeyring(granted, person, keys, grant);
      held = opened.keyring;
      await store.keepKeyring(account, held);
    }
    const sealed = await sealEntry(subtle, random, account, held, granted.invite, person);
    if (sealed === null) return { kind: 'failed' };
    const reply = await relay.post('/v1/keyring/key', keyBody(granted.invite, sealed, grant), readOk);
    // CL9: an entry for the invite already stored (an earlier answer lost, or another device's accept) is this one when it opens to this person key.
    if (reply.kind === 'refused' && reply.error === 'keyring-exists' && (await this.entryHeld(held, granted.invite, person))) return { kind: 'accepted', invite: granted.invite };
    return this.answered(granted, reply);
  }

  /** CL9: whether the relay's keyring holds `invite`'s entry, opening under `keyring` to `person`. */
  private async entryHeld(keyring: HeldKeyring, invite: string, person: Uint8Array<ArrayBuffer>): Promise<boolean> {
    const wire = await this.deps.relay.get('/v1/keyring', readKeyring);
    if (wire.kind !== 'ok') return false;
    const opened = (await openEntries(this.deps.subtle, this.deps.account, keyring, wire.value.keys)).get(invite);
    return opened?.length === person.length && opened.every((byte, index) => byte === person[index]);
  }

  /** CL1: a new keyring made with its first entry, under one grant; kept here once the relay took it. */
  private async makeKeyring(granted: Granted, person: Uint8Array<ArrayBuffer>, keys: PasswordKeys, grant: string): Promise<AcceptOutcome> {
    const { subtle, random, relay, store, account } = this.deps;
    const made = await newKeyring(subtle, random, account, keys);
    const sealed = made === null ? null : await sealEntry(subtle, random, account, made.keyring, granted.invite, person);
    if (made === null || sealed === null) return { kind: 'failed' };
    const reply = await relay.post('/v1/keyring', keyringBody(made.keyring.id, made.wrapped, keys.kdf, grant, { invite: granted.invite, sealed }), readOk);
    if (reply.kind === 'ok') await store.keepKeyring(account, made.keyring);
    return this.answered(granted, reply);
  }

  private answered(granted: Granted, reply: Reply<true>): AcceptOutcome {
    if (reply.kind === 'ok') return { kind: 'accepted', invite: granted.invite };
    return reply.kind === 'refused' ? { kind: 'refused', error: reply.error } : { kind: 'failed' };
  }

  /**
   * KG9, KW2: the keyring's wrap for a change of P to the password that gave `next`: the keyring this device holds, or
   * the relay's opened with `previous`; tested before it is given. Null when the account has no keyring; `failed` when it
   * can't be made (the relay unreachable, the record not opening, no old password to open it with).
   */
  async rewrap(next: PasswordKeys, previous: PasswordKeys | null): Promise<{ readonly id: string; readonly wrapped: string } | null | 'failed'> {
    const { subtle, random, relay, store, account } = this.deps;
    let held = await store.keyring(account);
    if (held === null) {
      const wire = await relay.get('/v1/keyring', readKeyring);
      if (wire.kind !== 'ok') return 'failed';
      if (wire.value.record === null) return null;
      if (previous === null) return 'failed';
      const opened = await openKeyring(subtle, account, previous.wrapping, wire.value);
      if (opened.kind !== 'opened') return 'failed';
      held = opened.keyring;
    }
    const wrapped = await rewrapKeyring(subtle, random, account, held, next);
    return wrapped === null ? 'failed' : { id: toBase64Url(held.id), wrapped: toBase64Url(wrapped) };
  }

  /** CC12: the cache brought to what the relay serves now; one check at a time (a second call waits for the first's). */
  check(): Promise<CheckOutcome> {
    // Deep-fix r5: asked again while one runs (an accept, a login), one more pass follows it, so what changed meanwhile
    // (a claim just made) is read; every ask meanwhile shares that pass.
    if (this.running !== null) {
      // Whichever way the running one ends (a storage refusal rejects it), the pass after it runs and is let go.
      const next = (): Promise<CheckOutcome> => {
        this.again = null;
        return this.check();
      };
      this.again ??= this.running.then(next, next);
      return this.again;
    }
    this.running = this.track(this.checkNow()).finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async checkNow(): Promise<CheckOutcome> {
    const { relay, store, account, now } = this.deps;
    const listed = await relay.get('/v1/invites', readClaims);
    if (listed.kind !== 'ok') {
      // CC8, CC9: nothing dropped but what has passed its end on this device's clock.
      await this.claims();
      return { kind: 'offline' };
    }
    const wire = await relay.get('/v1/keyring', readKeyring);
    if (wire.kind !== 'ok') {
      await this.claims();
      return { kind: 'offline' };
    }
    const persons = await this.persons(wire.value);
    const cached = new Map((await store.claims(account)).map((claim) => [claim.invite, claim]));
    const live = listed.value.filter((claim) => !isPast(claim, now()));
    const kept = new Set(live.map((claim) => claim.invite));
    // CC1, CC9: every claim the relay doesn't serve (or this clock has ended) dropped in one write.
    const gone = [...cached.keys()].filter((invite) => !kept.has(invite));
    if (gone.length > 0) await store.dropClaims(account, gone);
    const unavailable: string[] = [];
    for (const claim of live) {
      // Deep-fix r4: closed (a logout) or the account gone: no further request, so a logout never waits on the rest.
      if (!this.alive()) break;
      const person = persons.get(claim.invite) ?? cached.get(claim.invite)?.person ?? null;
      if (!(await this.refresh(claim, person, cached.get(claim.invite) ?? null))) unavailable.push(claim.invite);
    }
    return { kind: 'checked', unavailable };
  }

  /**
   * KG1, KG3: the person keys the relay's keyring holds, opened with the keyring this device holds. A keyring gone at the
   * relay (a reset by E) drops everything of the account's here; one of another ID (a reset, then a new keyring) drops
   * the key held, so nothing opens until the password is typed again.
   */
  private async persons(wire: WireKeyring): Promise<ReadonlyMap<string, Uint8Array<ArrayBuffer>>> {
    const { store, account, subtle } = this.deps;
    const held = await store.keyring(account);
    if (wire.record === null) {
      if (held !== null) await store.clear(account);
      return new Map();
    }
    if (held === null) return new Map();
    const same = wire.record.id.length === held.id.length && wire.record.id.every((byte, index) => byte === held.id[index]);
    if (!same) {
      await store.clear(account);
      return new Map();
    }
    return openEntries(subtle, account, held, wire.keys);
  }

  /** CC2–CC7, CC11, CC13: one listed claim's role keys, copies and notes brought up to date and kept; false when a copy was unavailable (CC13). */
  private async refresh(claim: ListedClaim, person: Uint8Array<ArrayBuffer> | null, previous: CachedClaim | null): Promise<boolean> {
    const { store, account } = this.deps;
    const base: CachedClaim = {
      invite: claim.invite,
      share: claim.share,
      role: claim.role,
      notes: claim.notes,
      export: claim.export,
      endsAt: claim.endsAt,
      chapters: claim.chapters,
      person,
      roleKeys: previous?.roleKeys ?? [],
      cursor: previous?.cursor ?? 0,
      held: previous?.held ?? [],
      reads: previous?.reads ?? '',
      checkedAt: this.deps.now(),
    };
    if (person === null) {
      // CC11: no key here yet: settings only, nothing downloaded.
      await store.keepClaim(account, base);
      return true;
    }
    const roleKeys = await this.roleKeys(claim, person, base.roleKeys);
    const available = await this.copies(claim, person);
    // NC1–NC5: what the claim reads now; a change (a role, a notes level, a role key gained or lost) pulls every note
    // again from the start, so a group's notes from before, or a note whose key came later, are never skipped, and the
    // notes of a group no longer read go.
    const reads = readsOf(claim, roleKeys);
    const pulled = reads === base.reads ? base : { ...base, cursor: 0, held: [] };
    const { cursor, held } = await this.notes({ ...pulled, roleKeys });
    await store.keepClaim(account, { ...base, roleKeys, cursor, held, reads });
    return available;
  }

  /** IN19: the role keys listed, opened with the person key; those held that the list no longer names dropped. */
  private async roleKeys(claim: ListedClaim, person: Uint8Array<ArrayBuffer>, held: readonly HeldRoleKey[]): Promise<readonly HeldRoleKey[]> {
    const { share, invite } = claim.ids;
    const opened: HeldRoleKey[] = [];
    for (const listed of claim.roleKeys) {
      const known = held.find((key) => key.group === listed.group && key.epoch === listed.epoch);
      // A key held at this group and epoch is the one its wraps hold (the relay can't change it: decision 49's AAD).
      const key = known === undefined ? await unwrapRoleKey(this.deps.subtle, person, { share, invite, group: listed.group, epoch: listed.epoch }, listed.wrap) : known.key;
      if (key !== null) opened.push({ group: listed.group, epoch: listed.epoch, key });
    }
    return opened;
  }

  /** CC3–CC7, CC13: each listed copy downloaded unless the one held is as late or later; those no longer listed dropped. */
  private async copies(claim: ListedClaim, person: Uint8Array<ArrayBuffer>): Promise<boolean> {
    const { store, account, relay } = this.deps;
    const held = new Map((await store.items(account, claim.invite)).map((item) => [item.item, item]));
    const listed = new Set(claim.copies.map((copy) => copy.item));
    const stale = [...held.keys()].filter((item) => !listed.has(item));
    if (stale.length > 0) await store.dropItems(account, claim.invite, stale);
    let available = true;
    for (const copy of claim.copies) {
      if (!this.alive()) break;
      const current = held.get(copy.item);
      // CC4, CC5: the same version, or a later one held (no rollback on this device).
      if (current !== undefined && !isLater(copy.version, current.version)) continue;
      const bytes = await relay.postForBytes('/v1/invites/copy', copyBody(claim.invite, copy.item), MAX_COPY_BYTES);
      if (bytes.kind !== 'ok') {
        if (bytes.kind === 'refused' && bytes.error === 'copy-unavailable') available = false;
        continue;
      }
      const item: CachedItem = { item: copy.item, version: copy.version, sealed: bytes.value, wrap: copy.wrap };
      // CC7: kept only once it opens and reads at its place.
      if ((await openItem(this.deps.subtle, claim, person, item)) !== null) await store.keepItem(account, claim.invite, item);
    }
    return available;
  }

  /** NV: the notes changed since the claim's cursor, opened and merged into those held (a tombstone takes its note away). */
  private async notes(claim: CachedClaim): Promise<{ readonly cursor: number; readonly held: readonly HeldNote[] }> {
    const held = new Map(claim.held.map((note) => [note.id, note]));
    let cursor = claim.cursor;
    while (this.alive()) {
      const page = await this.deps.relay.post('/v1/invites/notes', notesBody(claim.invite, cursor), readInviteeNotes);
      if (page.kind !== 'ok') break;
      for (const pulled of page.value.notes) {
        cursor = Math.max(cursor, pulled.seq);
        const id = toHex(pulled.id);
        const opened = pulled.body === null ? null : await openPulled(this.deps.subtle, claim, pulled);
        if (opened === null) held.delete(id);
        else held.set(id, opened);
      }
      if (!page.value.more) break;
    }
    return { cursor, held: [...held.values()].sort((a, b) => a.seq - b.seq) };
  }

  /** The claim's items opened (its manifest and chapters), by item; one that doesn't open is left out (CC7). */
  async open(invite: string): Promise<ReadonlyMap<string, InviteCopy>> {
    const { store, account } = this.deps;
    const claim = (await this.claims()).find((each) => each.invite === invite);
    if (claim?.person === null || claim === undefined) return new Map();
    const opened = new Map<string, InviteCopy>();
    for (const item of await store.items(account, invite)) {
      const copy = await openItem(this.deps.subtle, claim, claim.person, item);
      if (copy !== null) opened.set(item.item, copy);
    }
    return opened;
  }

  /** The claim `invite`'s manifest opened (Shared with me's card), or null when none is held or it doesn't open. */
  async manifest(invite: string): Promise<InviteManifest | null> {
    const { store, account } = this.deps;
    const claim = (await this.claims()).find((each) => each.invite === invite);
    const item = manifestOf(invite);
    if (claim?.person === null || claim === undefined || item === null) return null;
    const held = (await store.items(account, invite)).find((each) => each.item === item);
    const copy = held === undefined ? null : await openItem(this.deps.subtle, claim, claim.person, held);
    return copy?.kind === 'manifest' ? copy : null;
  }

  /**
   * NV: `draft` posted on the claim `invite`: a new note in {@link newNoteGroup}'s group, a reply in its first note's
   * group when the claim may post there; `edit` names one of its own notes to say anew (the same ID, group and epoch).
   */
  post(invite: string, draft: Draft, edit: string | null = null): Promise<NoteOutcome> {
    return this.track(this.posting(invite, draft, edit));
  }

  private async posting(invite: string, draft: Draft, edit: string | null): Promise<NoteOutcome> {
    const claim = (await this.claims()).find((each) => each.invite === invite);
    if (claim?.person === null || claim === undefined) return { kind: 'not-allowed' };
    const own = edit === null ? null : (claim.held.find((note) => note.id === edit && note.author === invite) ?? null);
    if (edit !== null && own === null) return { kind: 'not-allowed' };
    const group = own?.group ?? (draft.kind === 'reply' ? (claim.held.find((note) => note.id === draft.to)?.group ?? null) : newNoteGroup(claim));
    if (group === null || !mayPost(claim, group)) return { kind: 'not-allowed' };
    const key = group === 'person' ? { epoch: 0, key: claim.person } : latest(claim.roleKeys, group);
    if (key === null) return { kind: 'not-allowed' };
    const epoch = own?.epoch ?? key.epoch;
    const roleKey = group === 'person' ? claim.person : claim.roleKeys.find((each) => each.group === group && each.epoch === epoch)?.key;
    const share = idBytes(claim.share);
    const author = idBytes(invite);
    const id = own === null ? fresh(this.deps.random) : hexBytes(own.id);
    if (roleKey === undefined || share === null || author === null || id === null) return { kind: 'not-allowed' };
    const note = noteOf(draft, group);
    const place = { share, note: id, group, thread: group === 'person' ? author : null, epoch, author };
    const iv = new Uint8Array(12);
    this.deps.random(iv);
    const sealed = await sealNote(this.deps.subtle, roleKey, place, encodeShareNote(note), iv);
    const reply = await this.deps.relay.post('/v1/invites/note', noteBody(invite, id, group, epoch, sealed), readNoteSeq);
    if (reply.kind !== 'ok') return reply.kind === 'refused' ? { kind: 'refused', error: reply.error } : { kind: 'failed' };
    const held: HeldNote = { id: toHex(id), seq: reply.value, group, thread: group === 'person' ? invite : null, author: invite, epoch, at: own?.at ?? this.deps.now(), note };
    await this.keepNotes(claim, [...claim.held.filter((each) => each.id !== held.id), held]);
    return { kind: 'done' };
  }

  /** Decision 76: one of the invitee's own notes deleted at the relay, then here. */
  remove(invite: string, id: string): Promise<NoteOutcome> {
    return this.track(this.removing(invite, id));
  }

  private async removing(invite: string, id: string): Promise<NoteOutcome> {
    const claim = (await this.claims()).find((each) => each.invite === invite);
    const own = claim?.held.find((note) => note.id === id && note.author === invite);
    const bytes = hexBytes(id);
    if (claim === undefined || own === undefined || bytes === null) return { kind: 'not-allowed' };
    const reply = await this.deps.relay.post('/v1/invites/note/delete', noteRefBody(invite, bytes), readOk);
    if (reply.kind !== 'ok') return reply.kind === 'refused' ? { kind: 'refused', error: reply.error } : { kind: 'failed' };
    await this.keepNotes(claim, claim.held.filter((note) => note.id !== id));
    return { kind: 'done' };
  }

  private async keepNotes(claim: CachedClaim, held: readonly HeldNote[]): Promise<void> {
    await this.deps.store.keepClaim(this.deps.account, { ...claim, held });
  }
}

/** Table NC: what a claim's notes are read under: its role, its notes level and its role keys by group and epoch. */
export function readsOf(claim: Pick<CachedClaim, 'role' | 'notes'>, roleKeys: readonly Pick<HeldRoleKey, 'group' | 'epoch'>[]): string {
  return JSON.stringify([claim.role, claim.notes, roleKeys.map((key) => `${key.group}:${String(key.epoch)}`).sort()]);
}

/** The latest epoch held of `group`'s key (38e: post under the latest epoch held), or null. */
function latest(keys: readonly HeldRoleKey[], group: WireNoteGroup): { readonly epoch: number } | null {
  let best: HeldRoleKey | null = null;
  for (const key of keys) if (key.group === group && (best === null || key.epoch > best.epoch)) best = key;
  return best === null ? null : { epoch: best.epoch };
}

function fresh(random: Random): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(16);
  random(bytes);
  return bytes;
}

/**
 * NV, 38e's AQ: what `draft` posts in `group`. A passage noted in a group goes as its range alone (the group may hold
 * readers not given that chapter, so no quote or context leaves); in its own thread, with its quote.
 */
export function noteOf(draft: Draft, group: WireNoteGroup): ShareNote {
  if (draft.kind === 'reply') return draft;
  const anchor = draft.anchor === null || group === 'person' ? draft.anchor : { range: draft.anchor.range, quote: '', before: '', after: '' };
  return { kind: 'note', chapter: draft.chapter, anchor, title: draft.title, section: draft.section, text: draft.text };
}

/** GP5, CC7: `item` opened at its place under its wrap's key and read; null when any step doesn't hold. */
export async function openItem(subtle: SubtleCrypto, claim: Pick<CachedClaim, 'share' | 'invite'>, person: Uint8Array<ArrayBuffer>, item: CachedItem): Promise<InviteCopy | null> {
  const share = idBytes(claim.share);
  const invite = idBytes(claim.invite);
  if (share === null || invite === null) return null;
  const place = { share, item: item.item, version: item.version };
  const key = await unwrapCopyKey(subtle, person, invite, place, item.wrap, item.sealed);
  const plain = key === null ? null : await openCopy(subtle, key, place, item.sealed);
  if (plain === null) return null;
  try {
    const copy = readInviteCopy(plain);
    // GP5: a manifest only under the claim's own manifest item, a chapter only under a chapter's.
    return (copy.kind === 'manifest') === (item.item === manifestOf(claim.invite)) ? copy : null;
  } catch {
    return null;
  }
}

/** NV: a pulled note opened with the claim's key for its group (the writer's after its tag, which an invitee can't check). */
export async function openPulled(subtle: SubtleCrypto, claim: Pick<CachedClaim, 'share' | 'person' | 'roleKeys'>, pulled: PulledNote): Promise<HeldNote | null> {
  const share = idBytes(claim.share);
  const key = pulled.group === 'person' ? claim.person : (claim.roleKeys.find((each) => each.group === pulled.group && each.epoch === pulled.epoch)?.key ?? null);
  if (share === null || key === null || pulled.body === null) return null;
  const sealed = pulled.author === null ? pulled.body.slice(0, Math.max(0, pulled.body.length - WRITER_TAG_BYTES)) : pulled.body;
  const place = { share, note: pulled.id, group: pulled.group, thread: pulled.thread, epoch: pulled.epoch, author: pulled.author };
  const plain = await openNote(subtle, key, place, sealed);
  const note = plain === null ? null : readShareNote(plain);
  if (note === null) return null;
  return {
    id: toHex(pulled.id),
    seq: pulled.seq,
    group: pulled.group,
    thread: pulled.thread === null ? null : toBase64Url(pulled.thread),
    author: pulled.author === null ? null : toBase64Url(pulled.author),
    epoch: pulled.epoch,
    at: pulled.at,
    note,
  };
}
